// FRU-24081 (ANGULAR-77C): a lead submit whose answer never arrives (status 0) was lost: the original form sent the visitor to
// the thank-you page with no lead behind it. A submit is now sent again with the same submission id, which the server uses
// to give a repeat the first submit's reply instead of a second lead: Hope's submits through its own ledger, the original
// form's through its opt-in receipt (`submit_repeat_check`). Only the rate form's final submit and Hope's are covered;
// abandonment saves and the other forms submit exactly as before.

/** Tries again after no answer at all, and waits while the server says the first submit is still running. */
export const SUBMIT_NO_ANSWER_RETRIES = 2;
export const SUBMIT_PROCESSING_WAITS = 10;
/** The server's Retry-After for a submit it's still processing. */
export const SUBMIT_PROCESSING_WAIT_MS = 2000;

export const ORIGINAL_SUBMIT_MARKER_FIELD = 'submit_repeat_check';
export const ORIGINAL_SUBMIT_MARKER_VALUE = 'original';

/** The rate form's version, which the original form and Hope both send. */
const RATE_FORM_VERSION = '4';

export interface SubmitRetryPlan {
  /** What to send: the lead as is, or a copy carrying the original form's id and opt-in. */
  body: any;
  /** Whether a submit that got no answer may be sent again. */
  retry: boolean;
}

/** A new random submission id in the canonical form the server accepts (UUID version 4). */
export function newSubmissionId(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null;
  if (c && typeof c.randomUUID === 'function') { return c.randomUUID(); }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') { c.getRandomValues(bytes); }
  else { for (let i = 0; i < 16; i++) { bytes[i] = Math.floor(Math.random() * 256); } }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Whether this submit may be sent again, and what to send. Hope's final submit keeps its own id (the server's Hope ledger).
 * The original form's final submit gets a fresh id and the opt-in, on a copy, so the caller's lead is never changed. A submit
 * marked as Hope without an id (a visitor who switched from Hope to the form), an abandonment save, or another form's submit
 * is sent as before and never again.
 */
export function submitRetryPlan(lead: any, makeId: () => string = newSubmissionId): SubmitRetryPlan {
  const asIs = { body: lead, retry: false };
  if (!lead || typeof lead !== 'object' || lead.abandonment) { return asIs; }
  if (String(lead.form_version == null ? '' : lead.form_version) !== RATE_FORM_VERSION) { return asIs; }
  const hope = String(lead.leadAIMode == null ? '' : lead.leadAIMode) === '1';
  if (hope) {
    return { body: lead, retry: typeof lead.submission_id === 'string' && lead.submission_id.trim() !== '' };
  }
  return {
    body: { ...lead, submission_id: makeId(), [ORIGINAL_SUBMIT_MARKER_FIELD]: ORIGINAL_SUBMIT_MARKER_VALUE },
    retry: true,
  };
}
